import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Tooltip } from '@mui/material';
import { AlertTriangle, Info } from 'lucide-react';
import { DayFigures, useGetDaySummaryQuery } from '../../api/dashboard/dashboardApiSlice';
import { DayEdge, DayPreset } from '../../utils/bookingDay';
import { makeBookingsOnDayUrl, paths } from '../../routes';
import { roomUseCasesOf } from '../../utils/roomUseCases';

const DAY_PRESETS: readonly DayPreset[] = ['today', 'tomorrow'];

/**
 * "Today and tomorrow" (#3016): per day, the bookings starting, the bookings
 * ending and — for a tenant with a room use case — the starting ones still
 * awaiting approval. Counted by `GET /admin/dashboard/day-summary` over the
 * use cases the bookings list shows, with the day rule the list's `?day=`
 * filter mirrors (`utils/bookingDay.ts`), so a figure equals the list it opens.
 */
export default function DaySummarySection({ services, isRental }: Readonly<{ services?: string[], isRental: boolean }>) {
    const { t } = useTranslation();
    const useCases = services ?? [];
    // The same list the dashboard's "view all bookings" opens.
    const listPath = isRental ? paths.rentalBooking : paths.bookings;
    const { data, isLoading, isError } = useGetDaySummaryQuery(
        { useCases },
        { skip: useCases.length === 0, refetchOnMountOrArgChange: true },
    );
    if (useCases.length === 0) return null;
    const showApproval = roomUseCasesOf(services).length > 0;
    // Settled without both days (an answer of another shape) is a failure,
    // never an endless placeholder and never a zero.
    const failed = isError || (!isLoading && !(data?.today && data?.tomorrow));
    return (
        <div>
            <h2 className={'text-base font-semibold text-foreground mb-3'}>
                {t('dashboard.day_summary')}
            </h2>
            {failed ? (
                <div className={'bg-white rounded-2xl p-5 sm:p-6 shadow-sm'}>
                    <span role={'alert'} className={'flex items-center gap-2 text-sm font-medium text-foreground/70'}>
                        <AlertTriangle className={'w-4 h-4 shrink-0'} />
                        {t('dashboard.day_summary_failed')}
                    </span>
                </div>
            ) : (
                <div className={'grid grid-cols-1 sm:grid-cols-2 gap-4'}>
                    {DAY_PRESETS.map((preset) => (
                        <DayCard
                            key={preset}
                            preset={preset}
                            figures={isLoading ? undefined : data?.[preset]}
                            showApproval={showApproval}
                            listPath={listPath}
                        />
                    ))}
                </div>
            )}
        </div>
    );
}

interface DayCardProps {
    preset: DayPreset;
    /** Undefined while loading. */
    figures?: DayFigures;
    showApproval: boolean;
    listPath: string;
}

function DayCard({ preset, figures, showApproval, listPath }: Readonly<DayCardProps>) {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const dayLabel = t(`dashboard.day_${preset}`);
    const rows: Array<{ key: string, count?: number, edge: DayEdge, awaitingApproval: boolean }> = [
        { key: 'day_starting', count: figures?.starting, edge: 'start', awaitingApproval: false },
        { key: 'day_ending', count: figures?.ending, edge: 'end', awaitingApproval: false },
        ...(showApproval
            ? [{ key: 'day_awaiting_approval', count: figures?.awaitingApproval, edge: 'start' as DayEdge, awaitingApproval: true }]
            : []),
    ];
    return (
        <div className={'bg-white rounded-2xl p-5 sm:p-6 flex flex-col shadow-sm'}>
            <span className={'text-sm font-semibold text-foreground mb-2'}>
                {dayLabel}
            </span>
            <dl className={'flex flex-col divide-y divide-foreground/5'}>
                {rows.map((row) => (
                    <div key={row.key} className={'flex items-center justify-between gap-3 py-2'}>
                        <dt className={'flex items-center gap-1.5'}>
                            <span className={'text-sm text-foreground/60 font-medium'}>
                                {t(`dashboard.${row.key}`)}
                            </span>
                            <Tooltip title={t(`dashboard.${row.key}_desc`)} arrow placement={'top'}>
                                <span className={'inline-flex'}>
                                    <Info className={'w-3.5 h-3.5 text-foreground/40 cursor-help'} />
                                </span>
                            </Tooltip>
                        </dt>
                        <dd>
                            {row.count === undefined ? (
                                <span
                                    role={'status'}
                                    aria-label={t('dashboard.day_summary_loading')}
                                    className={'block h-7 w-8 rounded-md bg-foreground/10 animate-pulse'}
                                />
                            ) : (
                                <button
                                    type={'button'}
                                    aria-label={[dayLabel, t(`dashboard.${row.key}`)].join(' · ') + `: ${row.count}`}
                                    onClick={() => navigate(makeBookingsOnDayUrl(
                                        listPath,
                                        { preset, edge: row.edge },
                                        row.awaitingApproval,
                                    ))}
                                    className={'text-2xl font-bold text-foreground tabular-nums hover:text-primary hover:underline'}
                                >
                                    {row.count}
                                </button>
                            )}
                        </dd>
                    </div>
                ))}
            </dl>
        </div>
    );
}
